import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiTrash2, FiCopy, FiCheck } from 'react-icons/fi';
import { createEventShare, fetchEventShares, revokeEventShare } from '../apiService';
import './ShareDialog.css';

// Sections are stored flat on the server but presented grouped, because
// people think in terms of "the playbook" rather than its six parts.
const PLAYBOOK_SECTIONS = [
  { key: 'showOverview', label: 'Show Overview' },
  { key: 'generalInformation', label: 'General Information' },
  { key: 'travel', label: 'Travel Information' },
  { key: 'teamSchedule', label: 'Team Schedule' },
  { key: 'marketingSchedule', label: 'Marketing Schedule' },
  { key: 'boothMaterials', label: 'Booth Materials' },
];
const STANDALONE_SECTIONS = [
  { key: 'strategy', label: 'Strategy Recommendations' },
  { key: 'budget', label: 'Budget' },
  { key: 'tasks', label: 'Tasks' },
];

function ShareDialog({ event, user, onClose }) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [selected, setSelected] = useState([]);
  const [shares, setShares] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState('');

  const loadShares = useCallback(async () => {
    if (!event?.id) return;
    const res = await fetchEventShares(event.id);
    if (res.success) setShares(res.data || []);
  }, [event]);

  useEffect(() => { loadShares(); }, [loadShares]);

  const playbookKeys = PLAYBOOK_SECTIONS.map(s => s.key);
  const allPlaybookSelected = playbookKeys.every(k => selected.includes(k));

  function toggle(key) {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  function togglePlaybook() {
    setSelected(prev => allPlaybookSelected
      ? prev.filter(k => !playbookKeys.includes(k))
      : [...new Set([...prev, ...playbookKeys])]);
  }

  async function handleSend() {
    setResult(null);
    if (!recipientEmail.trim()) {
      setResult({ ok: false, msg: 'Enter an email address.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())) {
      setResult({ ok: false, msg: 'Enter a valid email address.' });
      return;
    }
    if (selected.length === 0) {
      setResult({ ok: false, msg: 'Choose at least one section to share.' });
      return;
    }

    setSending(true);
    const res = await createEventShare({
      eventId: event.id,
      userId: user.userId,
      recipientEmail: recipientEmail.trim(),
      recipientName: recipientName.trim(),
      sections: selected,
    });
    setSending(false);

    if (res.success) {
      // The share exists even when the email fails, so surface the link either
      // way rather than leaving the owner with nothing to pass on.
      setResult(res.emailed
        ? { ok: true, msg: `Sent to ${recipientEmail.trim()}.`, url: res.shareUrl }
        : { ok: true, warn: true, msg: 'Share created, but the email could not be sent. Copy the link below and send it yourself.', url: res.shareUrl });
      setRecipientEmail('');
      setRecipientName('');
      setSelected([]);
      loadShares();
    } else {
      setResult({ ok: false, msg: res.error || 'Could not create the share.' });
    }
  }

  async function handleRevoke(id) {
    await revokeEventShare(id);
    loadShares();
  }

  function copy(url) {
    navigator.clipboard?.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(''), 2000);
  }

  return (
    <div className="share-overlay">
      <div className="share-card">
        <div className="share-header">
          <h2>Share "{event?.eventName}"</h2>
          <button className="share-close" onClick={onClose}><FiX size={20} /></button>
        </div>

        <p className="share-intro">
          The person you share with can view the selected sections in their browser.
          They don't need a Zibit account.
        </p>

        <div className="share-field">
          <label>Recipient email</label>
          <input type="email" value={recipientEmail} placeholder="name@company.com"
            onChange={e => setRecipientEmail(e.target.value)} />
        </div>

        <div className="share-field">
          <label>Their name <span className="share-optional">(optional)</span></label>
          <input type="text" value={recipientName} placeholder="Sarah Jones"
            onChange={e => setRecipientName(e.target.value)} />
        </div>

        <div className="share-sections">
          <label className="share-group-label">What can they see?</label>

          <label className="share-check share-parent">
            <input type="checkbox" checked={allPlaybookSelected} onChange={togglePlaybook} />
            <strong>Playbook</strong>
          </label>
          <div className="share-children">
            {PLAYBOOK_SECTIONS.map(s => (
              <label key={s.key} className="share-check">
                <input type="checkbox" checked={selected.includes(s.key)} onChange={() => toggle(s.key)} />
                {s.label}
              </label>
            ))}
          </div>

          {STANDALONE_SECTIONS.map(s => (
            <label key={s.key} className="share-check share-parent">
              <input type="checkbox" checked={selected.includes(s.key)} onChange={() => toggle(s.key)} />
              <strong>{s.label}</strong>
            </label>
          ))}
        </div>

        {result && (
          <div className={`share-result ${result.ok ? (result.warn ? 'warn' : 'ok') : 'error'}`}>
            <div>{result.msg}</div>
            {result.url && (
              <div className="share-url-row">
                <code>{result.url}</code>
                <button onClick={() => copy(result.url)}>
                  {copiedUrl === result.url ? <FiCheck size={14} /> : <FiCopy size={14} />}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="share-actions">
          <button className="share-cancel" onClick={onClose}>Close</button>
          <button className="share-send" onClick={handleSend} disabled={sending}>
            {sending ? 'Sharing...' : 'Share'}
          </button>
        </div>

        {shares.length > 0 && (
          <div className="share-existing">
            <h4>Already shared with</h4>
            {shares.map(s => (
              <div key={s.id} className="share-existing-row">
                <div>
                  <div className="share-existing-email">{s.recipientEmail}</div>
                  <div className="share-existing-meta">
                    {s.sections.length} section{s.sections.length === 1 ? '' : 's'}
                    {s.lastViewed ? ' · viewed' : ' · not opened yet'}
                  </div>
                </div>
                <button className="share-revoke" onClick={() => handleRevoke(s.id)} title="Revoke access">
                  <FiTrash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ShareDialog;
